/**
 * Where a product keeps the data a channel usually owns.
 *
 * A channel owns its templates, its backgrounds and its default scenes: the
 * Social channel draws the Social banners, the Releases channel draws the
 * release ones. JetBrains Air is not that kind of product. It ships ONE
 * design — the frames on the `Release: JetBrains Air` page of the CoverBaker
 * Templates file — and that design is the same set of banners, sets, brand
 * treatments and layouts wherever a user reaches it from.
 *
 * So Air declares a HOME scope: the (channel, type) pair its data lives
 * under. Every channel that lists Air is an entry point to the same offering.
 * The entry channel still names the URL, the Channel dropdown and the
 * analytics event; the reads and writes below move to the home scope, so an
 * admin keeps one set of Air default scenes and one background list, and a
 * scene saved through one entry opens through the other.
 *
 * Keep this module free of React and of channel data: the server imports the
 * same resolution to size a saved scene's templates.
 */

export interface Scope {
  channel: string;
  type: string;
}

/**
 * The home scope per product code.
 *
 * Air's home is the Releases channel's Release stage, which is where its
 * default scenes and its background list already live. Moving the home would
 * orphan them.
 */
const HOME_SCOPE_BY_PRODUCT: Record<string, Scope> = {
  AIR: {channel: "releases", type: "release"},
};

/** Does this product carry its offering across channels? */
export function hasHomeScope(productValue: string | null | undefined): boolean {
  return Boolean(productValue) && productValue! in HOME_SCOPE_BY_PRODUCT;
}

/**
 * The (channel, type) pair to read and write data under.
 *
 * A product with no home scope keeps the caller's pair, so every other
 * product behaves exactly as before.
 */
export function homeScope(
  channelValue: string | null | undefined,
  typeValue: string | null | undefined,
  productValue: string | null | undefined
): Scope {
  const home = productValue ? HOME_SCOPE_BY_PRODUCT[productValue] : undefined;
  if (!home) return {channel: channelValue ?? "", type: typeValue ?? ""};
  return home;
}

/** The channel to read data under — the short form of `homeScope`. */
export function homeChannel(
  channelValue: string | null | undefined,
  productValue: string | null | undefined
): string {
  return homeScope(channelValue, null, productValue).channel;
}

/** The content type to read data under — the short form of `homeScope`. */
export function homeType(
  typeValue: string | null | undefined,
  productValue: string | null | undefined
): string {
  return homeScope(null, typeValue, productValue).type;
}
